import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router';

const PublickAWSIOTCoreSetting = () => {
  const { key } = useParams();
  const [port, setPort] = useState(null);
  const [writer, setWriter] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [message, setMessage] = useState('');
  const [log, setLog] = useState([]);
  const [formData, setFormData] = useState({
    domain: '',
    portVal: '',
    topic: '',
    client_id: '',
    username: '',
    password: ''
  });

  // Fetch data from API on component mount
  useEffect(() => {
    fetchAPIData();
    addLog("💡 Welcome to ESP32 AT Command Sender", 'info');
    addLog("📌 Step 1: Click 'Connect COM Port' to establish serial connection", 'info');
    addLog("📌 Step 2: Configure your MQTT settings above", 'info');
    addLog("📌 Step 3: Click 'Send AT Commands' to transmit configuration", 'info');
    addLog("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━", 'info');
  }, []);

  const fetchAPIData = async () => {
    try {
      const response = await fetch(`https://api.dqr-display-tms.eventum.co.in/api/Share/AWSIOTCore/publishId/File/${key}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch data from API');
      }
      
      const data = await response.json();
      
      // Assuming the API returns data in a format that matches your form fields
      // Adjust these mappings based on your actual API response structure
      setFormData({
        domain: data.domain ? `AT+RLCMD=JU_DOMAIN,W,${data.domain}` : '',
        portVal: data.port ? `AT+RLCMD=JU_PORT,W,${data.port}` : '',
        topic: data.topic ? `AT+RLCMD=JU_TOPIC,W,${data.topic}` : '',
        client_id: data.client_id ? `AT+RLCMD=JU_CLIENTID,W,${data.client_id}` : '',
        username: data.username ? `AT+RLCMD=JU_USERNAME,W,${data.username}` : '',
        password: data.password ? `AT+RLCMD=JU_PASSWORD,W,${data.password}` : ''
      });
      
      addLog("✅ API data loaded successfully", 'success');
    } catch (error) {
      console.error('Error fetching API data:', error);
      addLog("⚠️ Failed to load API data, using default values", 'warning');
      
      // Set default values if API fails
      setFormData({
        domain: 'AT+RLCMD=JU_DOMAIN,W,a2aevh9rbkb1sy-ats.iot.ap-south-1.amazonaws.com',
        portVal: 'AT+RLCMD=JU_PORT,W,8883',
        topic: 'AT+RLCMD=JU_TOPIC,W,BNKX000179',
        client_id: 'AT+RLCMD=JU_CLIENTID,W,BNKX000179----SN',
        username: 'AT+RLCMD=JU_USERNAME,W,test001',
        password: 'AT+RLCMD=JU_PASSWORD,W,123456'
      });
    }
  };

  const handleConnect = async () => {
    try {
      const selectedPort = await navigator.serial.requestPort();
      await selectedPort.open({ baudRate: 115200 });
      const portWriter = selectedPort.writable.getWriter();
      
      setPort(selectedPort);
      setWriter(portWriter);
      setIsConnected(true);
      addLog("✅ Serial port connected successfully", 'success');
    } catch (err) {
      addLog("❌ Error connecting to serial port: " + err.message, 'error');
    }
  };

  const sendCommand = async (cmd) => {
    if (!writer) return;
    
    const encoder = new TextEncoder();
    const command = cmd + "\r\n";
    await writer.write(encoder.encode(command));
    addLog("➡️ Sent: " + cmd, 'info');
    await new Promise(resolve => setTimeout(resolve, 300));
  };

  const handleSendAll = async () => {
    if (!writer) {
      addLog("⚠️ Please connect to COM port first", 'warning');
      return;
    }

    const commands = [
      formData.domain,
      formData.portVal,
      formData.topic,
      formData.client_id,
      formData.username,
      formData.password
    ];

    try {
      for (const cmd of commands) {
        if (cmd.trim()) {
          await sendCommand(cmd);
        }
      }
      addLog("✅ All commands sent successfully!", 'success');
    } catch (err) {
      addLog("❌ Error sending commands: " + err.message, 'error');
    }
  };

  const handleSendSingle = async (command) => {
    if (!writer) {
      addLog("⚠️ Please connect to COM port first", 'warning');
      return;
    }

    try {
      await sendCommand(command);
      addLog("✅ Command sent successfully!", 'success');
    } catch (err) {
      addLog("❌ Error sending command: " + err.message, 'error');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const formattedMessage = `[${timestamp}] ${message}`;
    setLog(prev => [...prev, { message: formattedMessage, type }]);
  };

  const handleCopyHttps = (key) => {
    setMessage("URL copied to clipboard!");
    const urlToCopy = `https://api.dqr-display-tms.eventum.co.in/api/Share/AWSIOTCore/publishId/File/${key}`;
    navigator.clipboard.writeText(urlToCopy)
      .then(() => {
        setMessage("URL copied to clipboard!");
        setTimeout(() => setMessage(""), 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  return (
    <div className="max-w-4xl mx-auto p-5 font-sans">
      <style jsx>{`
        .form-container {
          display: grid;
          gap: 20px;
          margin-bottom: 30px;
        }
        
        .form-group {
          display: flex;
          flex-direction: column;
        }
        
        .card {
          background: #fff;
          border-radius: 12px;
          padding: 25px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
          border: 1px solid #f0f0f0;
          margin-bottom: 20px;
        }
        
        .button-group {
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
        }
        
        .button-group button {
          flex: 1;
          min-width: 200px;
        }
        
        .log-container {
          height: 200px;
          overflow-y: auto;
          background: #f8f9fa;
          border: 2px solid #e1e5e9;
          border-radius: 12px;
          padding: 20px;
          font-family: 'Courier New', monospace;
          font-size: 0.9rem;
          line-height: 1.4;
          color: #2c3e50;
          box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
        }
        
        .log-container::-webkit-scrollbar {
          width: 8px;
        }
        
        .log-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        .log-container::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        
        .single-send-btn {
          background: linear-gradient(135deg, #f39c12, #e67e22);
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-left: 10px;
        }
        
        .single-send-btn:hover {
          background: linear-gradient(135deg, #e67e22, #d35400);
          transform: translateY(-1px);
        }
      `}</style>

      <h2 className="text-center mb-8 text-gray-800 font-light text-4xl tracking-tight">
        ESP32 Serial AT Command Sender
      </h2>

      {message && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {message}
        </div>
      )}

      <div className="card">
        <div className="button-group">
          <button
            onClick={handleConnect}
            className={`px-6 py-3 rounded-lg font-semibold text-white transition-all duration-300 ${
              isConnected
                ? 'bg-green-500 hover:bg-green-600'
                : 'bg-blue-500 hover:bg-blue-600'
            }`}
          >
            {isConnected ? '✅ Connected' : '🔌 Connect COM Port'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="form-container">
          <div className="form-group">
            <label className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">
              🌐 Domain
            </label>
            <div className="flex items-center">
              <input
                type="text"
                value={formData.domain}
                onChange={(e) => handleInputChange('domain', e.target.value)}
                className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:border-blue-500 focus:shadow-lg transition-all duration-300"
              />
              <button
                onClick={() => handleSendSingle(formData.domain)}
                className="single-send-btn"
              >
                Send
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">
              🔌 Port
            </label>
            <div className="flex items-center">
              <input
                type="text"
                value={formData.portVal}
                onChange={(e) => handleInputChange('portVal', e.target.value)}
                className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:border-blue-500 focus:shadow-lg transition-all duration-300"
              />
              <button
                onClick={() => handleSendSingle(formData.portVal)}
                className="single-send-btn"
              >
                Send
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">
              📡 Topic
            </label>
            <div className="flex items-center">
              <input
                type="text"
                value={formData.topic}
                onChange={(e) => handleInputChange('topic', e.target.value)}
                className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:border-blue-500 focus:shadow-lg transition-all duration-300"
              />
              <button
                onClick={() => handleSendSingle(formData.topic)}
                className="single-send-btn"
              >
                Send
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">
              🆔 Client ID
            </label>
            <div className="flex items-center">
              <input
                type="text"
                value={formData.client_id}
                onChange={(e) => handleInputChange('client_id', e.target.value)}
                className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:border-blue-500 focus:shadow-lg transition-all duration-300"
              />
              <button
                onClick={() => handleSendSingle(formData.client_id)}
                className="single-send-btn"
              >
                Send
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">
              👤 Username
            </label>
            <div className="flex items-center">
              <input
                type="text"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:border-blue-500 focus:shadow-lg transition-all duration-300"
              />
              <button
                onClick={() => handleSendSingle(formData.username)}
                className="single-send-btn"
              >
                Send
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">
              🔐 Password
            </label>
            <div className="flex items-center">
              <input
                type="text"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:border-blue-500 focus:shadow-lg transition-all duration-300"
              />
              <button
                onClick={() => handleSendSingle(formData.password)}
                className="single-send-btn"
              >
                Send
              </button>
            </div>
          </div>
        </div>

        <div className="button-group">
          <button
            onClick={handleSendAll}
            className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-all duration-300 hover:transform hover:-translate-y-1 hover:shadow-lg"
          >
            🚀 Send All AT Commands
          </button>
        </div>
      </div>

      <div className="card">
        <h3 className="mb-4 text-gray-800 font-light text-xl">📋 Command Log</h3>
        <div className="log-container">
          {log.map((entry, index) => (
            <div
              key={index}
              className={`mb-1 ${
                entry.type === 'success' ? 'text-green-600' :
                entry.type === 'error' ? 'text-red-600' :
                entry.type === 'warning' ? 'text-yellow-600' :
                'text-blue-600'
              }`}
            >
              {entry.message}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PublickAWSIOTCoreSetting;